import React from 'react';
import { Check, ChevronLeft, Eye, Mail, Settings } from 'react-feather';
import { ToolbarButton, ConfigEmptyState } from '../ConfigPages';
import { EtlPane, EtlKv, RunStatePill, EtlBanner } from '../EtlPages';
import { formatTimestamp } from './FileTrackingShared';
import { SheetAction, SheetActions } from './EtlMobileKit';

/**
 * Display status — trusts the backend. manualReview date-matching happens
 * server-side, so a stale flag from an earlier date can't override today's
 * correctly-processed file.
 */
const displayStatus = (record) => {
  if (!record) return 'pending';
  return {
    needs_review: 'needs_review',
    processed: 'processed',
    received: 'pending',
    pending: 'pending',
    missed: 'missed',
    failed: 'failed',
  }[record.status] || 'pending';
};

/** One stage of the pipeline, on the vertical track. */
function TimelineStep({ title, time, done, last }) {
  return (
    <li className={`ep-tl-item${done ? ' ep-done' : ''}${last ? ' ep-last' : ''}`}>
      <span className="ep-tl-dot" aria-hidden="true">{done ? <Check size={9} /> : null}</span>
      <span className="ep-tl-text">
        <span className="ep-tl-title">{title}</span>
        <span className="ep-tl-time">{time || 'Pending'}</span>
      </span>
    </li>
  );
}

/**
 * Detail pane for one tracked file: where it came from, how far through the
 * pipeline it got, and what to do about it.
 *
 * `mobile` (phones, shown full-screen) moves Email out of the head and lays
 * every action out as a full-width 48px row instead of a wrapping button row.
 */
export default function CurrentStatusDetailPanel({
  record, onEmail, onViewFile, onMarkReviewed, onConfigure, onClose, mobile = false,
}) {
  if (!record) {
    return (
      <EtlPane>
        <ConfigEmptyState
          title="Select a file"
          body="Pick a row to see where it came from, how far it got, and what to do next."
        />
      </EtlPane>
    );
  }

  const status = displayStatus(record);
  const locationName = record.metadata?.locationName || record.locationName || `Hotel ${record.hotelId}`;
  const receivedAt = record.reception?.receivedAt || record.tracking?.lastReceived;
  const processedAt = record.processing?.completedAt || record.tracking?.lastSuccessfullyProcessed;
  const rows = record.processing?.rowsInserted ?? record.tracking?.lastRowsProcessed;
  const alertCount = record.alerts?.history?.length || 0;
  const canView = !!(record.reception?.blobUrl || record.tracking?.lastProcessedFile);
  const needsReview = record.manualReview?.required && !record.manualReview?.cleared;
  const processingError = record.processing?.error?.message
    || record.tracking?.lastProcessingError?.error;

  const steps = [];
  if (receivedAt) steps.push({ title: 'File received', time: formatTimestamp(receivedAt), done: true });
  if (receivedAt && processedAt) steps.push({ title: 'Processing started', time: formatTimestamp(receivedAt), done: true });
  if (processedAt) steps.push({ title: 'Processing complete', time: formatTimestamp(processedAt), done: true });
  if (needsReview) steps.push({ title: 'Awaiting review', time: 'Requires user action', done: false });
  if (!steps.length) steps.push({ title: 'Awaiting file', time: 'Not yet received', done: false });

  return (
    <EtlPane
      title={locationName}
      actions={(
        <>
          {onClose ? (
            <ToolbarButton small variant="ghost" icon={ChevronLeft} onClick={onClose} className="ep-only-narrow">
              Back
            </ToolbarButton>
          ) : null}
          {onEmail && !mobile ? (
            <ToolbarButton small icon={Mail} onClick={() => onEmail(record)}>Email</ToolbarButton>
          ) : null}
        </>
      )}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <RunStatePill status={status} icon large />
        <span className="cp-code">
          {record.hotelId} · {record.displayName || record.fileType}
          {record.auditCode && record.auditCode !== record.fileType ? ` (${record.auditCode})` : ''}
        </span>
      </div>

      <EtlKv
        rows={[
          { label: 'Source', value: record.reception?.receivedFrom || 'Email' },
          { label: 'Rows', value: rows != null ? Number(rows).toLocaleString() : '—', mono: true },
          { label: 'Assigned', value: record.assignedTo || '—' },
          { label: 'Alerts', value: alertCount > 0 ? `${alertCount} sent` : 'None' },
        ]}
      />

      <div className="ep-section" style={{ marginTop: 16 }}>
        <div className="ep-section-head"><h3>Pipeline</h3></div>
        <ol className="ep-tl">
          {steps.map((step, i) => (
            <TimelineStep key={step.title} {...step} last={i === steps.length - 1} />
          ))}
        </ol>
      </div>

      {processingError ? (
        <div style={{ marginTop: 14 }}>
          <EtlBanner tone="error" title="Processing error">{processingError}</EtlBanner>
        </div>
      ) : null}

      {needsReview ? (
        <div style={{ marginTop: 14 }}>
          <EtlBanner tone="info" title="Manual review required">
            {record.manualReview?.reason || 'Date mismatch detected'}
          </EtlBanner>
        </div>
      ) : null}

      {mobile ? (
        <div style={{ marginTop: 18 }}>
          <SheetActions label="File actions">
            {status === 'needs_review' && onMarkReviewed ? (
              <SheetAction icon={Check} tone="primary" onClick={() => onMarkReviewed(record)}>
                Mark as reviewed
              </SheetAction>
            ) : null}
            {canView && onViewFile ? (
              <SheetAction icon={Eye} onClick={() => onViewFile(record)}>View file</SheetAction>
            ) : null}
            {onEmail ? (
              <SheetAction icon={Mail} onClick={() => onEmail(record)}>Email about this file</SheetAction>
            ) : null}
            {onConfigure ? (
              <SheetAction icon={Settings} onClick={() => onConfigure(record)}>Alert schedule and recipients</SheetAction>
            ) : null}
          </SheetActions>
        </div>
      ) : (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
        {canView && onViewFile ? (
          <ToolbarButton icon={Eye} onClick={() => onViewFile(record)}>View file</ToolbarButton>
        ) : null}
        {onConfigure ? (
          <ToolbarButton icon={Settings} onClick={() => onConfigure(record)}>Tracking options</ToolbarButton>
        ) : null}
        {status === 'needs_review' && onMarkReviewed ? (
          <ToolbarButton variant="primary" icon={Check} onClick={() => onMarkReviewed(record)}>
            Mark as reviewed
          </ToolbarButton>
        ) : null}
      </div>
      )}
    </EtlPane>
  );
}
